import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { RunAnalysisHeaderButton } from '@/app/components/features/evaluations/RunAnalysisButton';
import {
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { CheckListIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { ExtractionsPageContent } from '@/app/components/features/evaluations/Extractions/ExtractionsPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSetAtom } from 'jotai';
import { createExtractionOpenAtom } from '@/app/components/features/evaluations/Extractions/store';
import { getEPADetailsQuery } from '@/app/hooks/queries/useEarlyPhaseAssessment';
import { useSuspenseQuery } from '@tanstack/react-query';
import { validator } from '@/app/validator';
import z from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/early-phase-assessments/$earlyPhaseAssessmentId/_layout/(main)/extractions/',
)({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      id: z.string().optional(),
      extractionId: z.string().optional(),
      queryId: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations('pages.epaHome');
  const tFeature = useTranslations('features.extractions');
  const { earlyPhaseAssessmentId, projectId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getEPADetailsQuery(earlyPhaseAssessmentId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const summaryType = SummarizationNamespaces.EARLY_PHASE_ASSESSMENT;
  const setIsNewModalOpen = useSetAtom(createExtractionOpenAtom);

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
          params={{ projectId, earlyPhaseAssessmentId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <HugeiconsIcon icon={CheckListIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <RunAnalysisHeaderButton
          entityId={earlyPhaseAssessmentId}
          documentType={DocumentTypes.EARLY_PHASE_ASSESSMENT}
          onClick={() => setIsNewModalOpen(true)}
          aria-label={tFeature('buttons.create')}
        >
          <HugeiconsIcon icon={PlusSignIcon} />
        </RunAnalysisHeaderButton>
      </Header>
      <ExtractionsPageContent
        summaryType={summaryType}
        evaluationId={earlyPhaseAssessmentId}
        evalLanguage={evaluation.language}
      />
    </>
  );
}
